import EmberService, { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { VerificationObjectType, VerificationStatus } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { capitalize } from 'webapp/utils/common';
import {
	buildUnverifiedSkeleton,
	getVerificationEntityDisplayName,
	isVerified,
	type VerificationEntityLabel,
	type VerificationInfo,
} from 'webapp/utils/verification';

export interface VerificationChangedEventData {
	objectType: VerificationObjectType;
	objectId: string;
	// null once the verification is cleared
	status: VerificationStatus | null;
	// the BE's verification record, null once it is cleared; events sent before the BE added it don't carry it
	verification?: VerificationInfo | null;
	context?: Record<string, unknown>; // passed on as BE sends it
}

export default class VerificationService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('socket') declare socketService: Service.Socket;
	@service('request') declare requestService: Service.Request;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	async toggleVerification(
		verificationInfo: VerificationInfo,
		entityLabel: VerificationEntityLabel,
		entityName?: string,
		onUpdate?: (updated: VerificationInfo) => void,
	): Promise<VerificationStatus | null> {
		const { objectType, objectId } = verificationInfo;
		const isCurrentlyVerified = isVerified(verificationInfo);
		const endpoint = `/object-verifications/${objectType}/${encodeURIComponent(objectId)}`;
		const newStatus = isCurrentlyVerified
			? VerificationStatus.UNVERIFIED
			: VerificationStatus.VERIFIED;

		const previous = { ...verificationInfo };
		let savedStatus: VerificationStatus | null = null;

		// onUpdate must fire BEFORE in-place mutation so BufferedModel sees the
		// original savedValue as different from the new value.
		onUpdate?.({ ...verificationInfo, status: newStatus });
		verificationInfo.status = newStatus;

		try {
			const reply = await this.requestService.api<VerificationInfo>('POST', endpoint, {
				status: newStatus,
			});
			if (reply) {
				Object.assign(verificationInfo, reply);
			}

			// what the server holds, kept even if a later step throws and the catch rolls the record back
			savedStatus = verificationInfo.status;

			onUpdate?.({ ...verificationInfo });

			const event = isCurrentlyVerified
				? TrackingEvent.VERIFICATION_REMOVED
				: TrackingEvent.VERIFICATION_ADDED;
			this.analyticsService.event(event, { feature: entityLabel });

			const displayName = entityName
				? `<strong>${entityName}</strong>`
				: capitalize(getVerificationEntityDisplayName(entityLabel));
			if (isCurrentlyVerified) {
				this.infolayerService.info(`Verification has been removed from ${displayName}.`);
			} else {
				this.infolayerService.ok(
					`${displayName} has been set as verified ${getVerificationEntityDisplayName(entityLabel)}.`,
				);
			}
		} catch {
			verificationInfo.status = previous.status;
			verificationInfo.modified = previous.modified;
			verificationInfo.modifiedBy = previous.modifiedBy;
			onUpdate?.({ ...verificationInfo });
			const action = isCurrentlyVerified ? 'unverify' : 'verify';
			this.infolayerService.error(
				`Failed to ${action} ${entityName || getVerificationEntityDisplayName(entityLabel)}.`,
			);
		}

		// after the try/catch: a throwing Genie listener must not show "Failed to …" for a saved verification
		if (savedStatus !== null) {
			this.announceMetricVerification(objectType, objectId, savedStatus);
		}

		return savedStatus;
	}

	handlePusherEvent(data: VerificationChangedEventData): void {
		const { objectType, objectId, status, verification, context } = data;
		// A cleared verification becomes an unverified skeleton, not undefined: the boards' and reports' Verify
		// actions are disabled without a verificationInfo. An event without the record leaves the created / modified
		// info out until the record is reloaded.
		const verificationInfo = verification ?? {
			...buildUnverifiedSkeleton(String(objectId), objectType),
			status: status ?? VerificationStatus.UNVERIFIED,
		};

		if (objectType === VerificationObjectType.DATASET) {
			this.mmSourcesService
				.findModel(String(objectId))
				?.directPropUpdate({ verificationInfo });
		} else if (objectType === VerificationObjectType.BOARD) {
			this.mmBoardsService
				.findModel(String(objectId))
				?.directPropUpdate({ verificationInfo });
		} else if (objectType === VerificationObjectType.REPORT) {
			this.mmReportsService
				.findModel(String(objectId))
				?.directPropUpdate({ verificationInfo });
		} else if (
			objectType === VerificationObjectType.CUSTOM_QUERY ||
			objectType === VerificationObjectType.CALCULATED_METRIC ||
			objectType === VerificationObjectType.BASIC_METRIC
		) {
			this.updateMetricVerification(objectId, verificationInfo);
		}

		// not through the store: the Genie card needs it even when the metric isn't loaded
		this.announceMetricVerification(objectType, objectId, status);

		this.eventManagerService.trigger(AppEvent.VERIFICATION_CHANGED, {
			objectType,
			objectId,
			status,
			verification,
			context,
		});
	}

	private announceMetricVerification(
		objectType: VerificationObjectType,
		objectId: string,
		status: VerificationStatus | null,
	): void {
		if (
			objectType !== VerificationObjectType.BASIC_METRIC &&
			objectType !== VerificationObjectType.CUSTOM_QUERY &&
			objectType !== VerificationObjectType.CALCULATED_METRIC
		) {
			return;
		}

		let target: { key: string; spaceAccessId: number };
		try {
			const { key, spaceAccessId } = JSON.parse(objectId) as {
				key?: unknown;
				spaceAccessId?: unknown;
			};
			// the card matches strictly on a string key and a number spaceAccessId
			if (typeof key !== 'string' || typeof spaceAccessId !== 'number') {
				return;
			}
			target = { key, spaceAccessId };
		} catch {
			/* skip unparseable objectIds */
			return;
		}

		this.socketService.announceMetricVerification({
			...target,
			isVerified: status === VerificationStatus.VERIFIED,
		});
	}

	private updateMetricVerification(objectId: string, verificationInfo: VerificationInfo): void {
		try {
			const parsed = JSON.parse(objectId) as { key: string; spaceAccessId: number };
			const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
				parsed.key,
				parsed.spaceAccessId,
			);
			metric?.directPropUpdate({ verificationInfo });
		} catch {
			/* skip unparseable objectIds */
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		verification: VerificationService;
	}
}
